import { useEffect, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { getMyProfile, createProfile } from '@/lib/api/profiles';
import { identifyUser, resetUser, track } from '@/lib/analytics';
import { usePresence } from '@/lib/hooks/usePresence';
import { useLastSeenWriter } from '@/lib/hooks/useLastSeenWriter';

async function syncProfile(_userId: string, userMetadata: Record<string, unknown>) {
  const store = useAuthStore.getState();
  store.setProfileLoaded(false);
  try {
    let profile = await getMyProfile();
    if (!profile && userMetadata?.display_name) {
      // Fresh signup — build profile from the metadata stashed during sign-up.
      track({ event: 'signed_up', props: { industry: userMetadata.industry as string | undefined } });
      profile = await createProfile({
        display_name: String(userMetadata.display_name),
        handle: '', // createProfile generates a handle when empty
        headline: null,
        bio: null,
        current_city: userMetadata.current_city ? String(userMetadata.current_city) : null,
        current_country: userMetadata.current_country ? String(userMetadata.current_country) : null,
        origin_city: null,
        origin_country: null,
        industry: userMetadata.industry ? String(userMetadata.industry) : null,
        role_title: userMetadata.role_title ? String(userMetadata.role_title) : null,
        company: userMetadata.company ? String(userMetadata.company) : null,
        linkedin_url: userMetadata.linkedin_url ? String(userMetadata.linkedin_url) : null,
        website_url: null,
      });
    }
    store.setProfile(profile);
    if (profile) {
      identifyUser(profile.id, {
        name:         profile.display_name,
        industry:     profile.industry,
        status:       profile.status,
        verification: profile.verification,
      });
    }
  } catch {
    store.setProfile(null);
  } finally {
    store.setProfileLoaded(true);
  }
}

export function AuthInit({ children }: { children: ReactNode }) {
  usePresence();
  useLastSeenWriter();

  useEffect(() => {
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      useAuthStore.getState().setSession(data.session);
      useAuthStore.getState().setLoaded(true);
      if (data.session?.user) {
        void syncProfile(data.session.user.id, data.session.user.user_metadata ?? {});
      } else {
        useAuthStore.getState().setProfileLoaded(true);
      }
    }).catch(() => {
      if (!active) return;
      // Supabase unreachable (e.g. env vars not set) — mark as loaded so the
      // app renders in an unauthenticated state rather than spinning forever.
      useAuthStore.getState().setLoaded(true);
      useAuthStore.getState().setProfileLoaded(true);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      useAuthStore.getState().setSession(session);
      useAuthStore.getState().setAuthEvent(event);

      if (event === 'SIGNED_IN' && session?.user) {
        track({ event: 'signed_in' });
        void syncProfile(session.user.id, session.user.user_metadata ?? {});
      } else if (event === 'SIGNED_OUT') {
        track({ event: 'signed_out' });
        resetUser();
        useAuthStore.getState().setProfile(null);
        useAuthStore.getState().setProfileLoaded(true);
      }
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);
  return <>{children}</>;
}
